import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, FileText, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { Spinner } from '@/components/ui';
import { Button } from '@/components/ui';
import { invalidateLoanDocumentQueries } from '@/hooks/use-documents';
import {
  useAddInsurance,
  useAddValuation,
  useInsurancePolicies,
  useValuations,
} from '@/hooks/use-valuations';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { handleMutationError } from '@/lib/error-handling';
import { fmtCurrency as fmt } from '@/lib/format';
import { toast } from '@/stores/toast-store';

const MAX_ATTACHMENT_SIZE_BYTES = 20 * 1024 * 1024;
const MAX_ATTACHMENT_SIZE_LABEL = '20 MB';
const ATTACHMENT_ACCEPT = '.pdf,.doc,.docx,.jpg,.jpeg,.png';

interface ValuationInsuranceTabProps {
  securityId: string;
  securityType: string;
  currentValue?: number;
  loanAmount?: number;
  loanId?: string;
}

export function ValuationInsuranceTab({
  securityId,
  securityType: _securityType,
  currentValue = 0,
  loanAmount = 0,
  loanId,
}: ValuationInsuranceTabProps) {
  const queryClient = useQueryClient();
  const { data: valuations, isLoading: valLoading } = useValuations(securityId);
  const { data: policies, isLoading: polLoading } = useInsurancePolicies(securityId);
  const addValuation = useAddValuation();
  const addInsurance = useAddInsurance();

  const [showValuationForm, setShowValuationForm] = useState(false);
  const [valuationAmount, setValuationAmount] = useState<number>(currentValue);
  const [valuationDate, setValuationDate] = useState(new Date().toISOString().split('T')[0]);
  const [valuationType, setValuationType] = useState('PROFESSIONAL');
  const [valuer, setValuer] = useState('');
  const [valuationNotes, setValuationNotes] = useState('');

  const [showInsuranceForm, setShowInsuranceForm] = useState(false);
  const [policyNumber, setPolicyNumber] = useState('');
  const [insurer, setInsurer] = useState('');
  const [insuranceType, setInsuranceType] = useState('COMPREHENSIVE');
  const [cover, setCover] = useState<number>(0);
  const [premium, setPremium] = useState<number>(0);
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState('');

  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<{ name: string; id: string }[]>([]);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !loanId) return;
    if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      setUploadError(`File too large. Maximum size is ${MAX_ATTACHMENT_SIZE_LABEL}.`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setUploadError('');
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', 'VALUATION');
      formData.append('description', `Valuation report - ${file.name}`);
      const { data } = await api.post(`/loans/${loanId}/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadedFiles(prev => [...prev, { name: file.name, id: data.id }]);
      invalidateLoanDocumentQueries(queryClient, loanId);
      toast.success(`Uploaded ${file.name}`);
    } catch (err: any) {
      if (err?.response?.status === 413) {
        setUploadError(`File too large. Maximum size is ${MAX_ATTACHMENT_SIZE_LABEL}.`);
      } else if (err?.response?.status === 429) {
        toast.error('Too many requests — please wait a moment then try again');
      } else {
        handleMutationError(err, 'Upload failed');
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const isLoading = valLoading || polLoading;

  const handleAddValuation = async () => {
    try {
      await addValuation.mutateAsync({
        securityId,
        amount: valuationAmount,
        date: valuationDate,
        type: valuationType,
        valuer,
        notes: valuationNotes,
      });
      setShowValuationForm(false);
      setValuationAmount(currentValue);
      setValuationNotes('');
      setValuer('');
    } catch (error: any) {
      handleMutationError(error, 'Failed to add valuation');
    }
  };

  const handleAddInsurance = async () => {
    try {
      await addInsurance.mutateAsync({
        securityId,
        number: policyNumber,
        insurer,
        type: insuranceType,
        cover,
        premium,
        startDate,
        endDate,
      });
      setShowInsuranceForm(false);
      setPolicyNumber('');
      setInsurer('');
      setCover(0);
      setPremium(0);
    } catch (error: any) {
      handleMutationError(error, 'Failed to add insurance policy');
    }
  };

  const latestValuation = valuations?.[0];
  const ltv = latestValuation ? (loanAmount / latestValuation.amount) * 100 : 0;

  const daysUntilExpiry = (expiryDate: string) => {
    const days = Math.floor(
      (new Date(expiryDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24),
    );
    return days;
  };

  const activeInsurance =
    policies?.filter((p: any) => {
      const now = new Date();
      const end = new Date(p.endDate);
      return end > now;
    }) ?? [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-400">
        <Spinner size="lg" className="w-5 h-5 animate-spin mr-2" />
        Loading valuation and insurance information…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Valuations Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Valuations</h3>
          <button
            onClick={() => setShowValuationForm(!showValuationForm)}
            className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
          >
            {showValuationForm ? 'Cancel' : 'Add Valuation'}
          </button>
        </div>

        {/* Current Valuation */}
        {latestValuation && (
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-gray-500 mb-1">Latest Valuation</p>
                <p className="text-lg font-semibold text-gray-900">{fmt(latestValuation.amount)}</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {new Date(latestValuation.date).toLocaleDateString('en-AU')}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">LTV</p>
                <p
                  className={cn(
                    'text-lg font-semibold',
                    ltv > 80 ? 'text-red-600' : ltv > 70 ? 'text-amber-600' : 'text-green-600',
                  )}
                >
                  {ltv.toFixed(1)}%
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Type</p>
                <p className="text-sm font-medium text-gray-900">{latestValuation.type}</p>
              </div>
            </div>
          </div>
        )}

        {/* Add Valuation Form */}
        {showValuationForm && (
          <div className="bg-gray-50 rounded-lg p-4 mb-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Amount (AUD)</label>
                <input
                  type="number"
                  value={valuationAmount}
                  onChange={e => setValuationAmount(parseFloat(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                <input
                  type="date"
                  value={valuationDate}
                  onChange={e => setValuationDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                <select
                  value={valuationType}
                  onChange={e => setValuationType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option>PROFESSIONAL</option>
                  <option>BANK_VALUATION</option>
                  <option>INFORMAL</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Valuer</label>
                <input
                  type="text"
                  value={valuer}
                  onChange={e => setValuer(e.target.value)}
                  placeholder="Valuer name…"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
              <textarea
                value={valuationNotes}
                onChange={e => setValuationNotes(e.target.value)}
                placeholder="Add any notes…"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                rows={2}
              />
            </div>
            <Button
              variant="primary"
              onClick={handleAddValuation}
              disabled={addValuation.isPending}
              className="w-full px-4 py-2 text-white rounded-md text-sm font-medium hover: disabled:opacity-50 transition-colors"
            >
              {addValuation.isPending ? 'Adding…' : 'Add Valuation'}
            </Button>
          </div>
        )}

        {/* Valuation Document Upload */}
        {loanId && (
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-700">Valuation Documents</p>
              <label className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors">
                <Upload className="w-4 h-4" />
                {uploading ? 'Uploading…' : 'Upload PDF'}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ATTACHMENT_ACCEPT}
                  onChange={handleFileUpload}
                  disabled={uploading}
                  className="hidden"
                />
              </label>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Allowed: PDF, DOC, DOCX, JPG, JPEG, PNG. Max {MAX_ATTACHMENT_SIZE_LABEL}.
            </p>
            {uploadError && <p className="text-xs font-medium text-red-600 mt-1">{uploadError}</p>}
            {uploadedFiles.length > 0 && (
              <div className="space-y-1 mt-2">
                {uploadedFiles.map(f => (
                  <div key={f.id} className="flex items-center gap-2 text-sm text-gray-600">
                    <FileText className="w-4 h-4 text-indigo-500" />
                    <span>{f.name}</span>
                  </div>
                ))}
              </div>
            )}
            {uploadedFiles.length === 0 && (
              <p className="text-xs text-gray-400 mt-1">
                Attach valuation PDFs to support security information
              </p>
            )}
          </div>
        )}

        {/* Valuation History */}
        {valuations && valuations.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                  <th className="pb-2 font-medium">Date</th>
                  <th className="pb-2 font-medium text-right">Amount</th>
                  <th className="pb-2 font-medium">Type</th>
                  <th className="pb-2 font-medium">Valuer</th>
                </tr>
              </thead>
              <tbody>
                {valuations.slice(0, 5).map((v: any, i: number) => (
                  <tr
                    key={i}
                    className="border-b border-gray-50 hover:bg-gray-50 transition-colors"
                  >
                    <td className="py-2.5 text-gray-600">
                      {new Date(v.date).toLocaleDateString('en-AU')}
                    </td>
                    <td className="py-2.5 text-right font-medium text-gray-900">{fmt(v.amount)}</td>
                    <td className="py-2.5 text-gray-600">{v.type}</td>
                    <td className="py-2.5 text-gray-600">{v.valuer || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-400 text-center py-4">No valuations recorded</p>
        )}
      </div>

      {/* Insurance Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Insurance Policies</h3>
          <button
            onClick={() => setShowInsuranceForm(!showInsuranceForm)}
            className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
          >
            {showInsuranceForm ? 'Cancel' : 'Add Policy'}
          </button>
        </div>

        {/* Add Insurance Form */}
        {showInsuranceForm && (
          <div className="bg-gray-50 rounded-lg p-4 mb-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Policy Number
                </label>
                <input
                  type="text"
                  value={policyNumber}
                  onChange={e => setPolicyNumber(e.target.value)}
                  placeholder="Policy number…"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Insurer</label>
                <input
                  type="text"
                  value={insurer}
                  onChange={e => setInsurer(e.target.value)}
                  placeholder="Insurer name…"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                <select
                  value={insuranceType}
                  onChange={e => setInsuranceType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option>COMPREHENSIVE</option>
                  <option>BASIC</option>
                  <option>THIRD_PARTY</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Cover (AUD)</label>
                <input
                  type="number"
                  value={cover}
                  onChange={e => setCover(parseFloat(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Premium (AUD)
                </label>
                <input
                  type="number"
                  value={premium}
                  onChange={e => setPremium(parseFloat(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <Button
              variant="primary"
              onClick={handleAddInsurance}
              disabled={addInsurance.isPending}
              className="w-full px-4 py-2 text-white rounded-md text-sm font-medium hover: disabled:opacity-50 transition-colors"
            >
              {addInsurance.isPending ? 'Adding…' : 'Add Policy'}
            </Button>
          </div>
        )}

        {/* Active Policies */}
        {activeInsurance && activeInsurance.length > 0 ? (
          <div className="space-y-3">
            {activeInsurance.map((policy: any, i: number) => {
              const days = daysUntilExpiry(policy.endDate);
              const isExpiring = days < 30;
              return (
                <div
                  key={i}
                  className={cn(
                    'border rounded-lg p-4',
                    isExpiring ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-gray-50',
                  )}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-medium text-gray-900">{policy.number}</p>
                      <p className="text-sm text-gray-600">{policy.insurer}</p>
                    </div>
                    {isExpiring && (
                      <div className="flex items-center gap-1 text-red-700">
                        <AlertTriangle className="w-4 h-4" />
                        <span className="text-xs font-medium">{days} days</span>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-xs text-gray-500">Cover</p>
                      <p className="font-medium text-gray-900">{fmt(policy.cover)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Premium</p>
                      <p className="font-medium text-gray-900">{fmt(policy.premium)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Expires</p>
                      <p className="font-medium text-gray-900">
                        {new Date(policy.endDate).toLocaleDateString('en-AU')}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-gray-400 text-center py-4">No active policies</p>
        )}
      </div>
    </div>
  );
}
